import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../molecules/PageHeader';
import SelectFilter from '../../organisms/SelectFilter';
import { toast } from 'react-toastify';
import appConfig from '../../../config/app-config';

const { address: ROUTER_API_URL, key: ROUTER_API_KEY } = appConfig.apis.routerApiEnv;

const API_KEY = ROUTER_API_KEY;
const BASE_URL = `${ROUTER_API_URL}/recommendation-engine/rules`;

interface Condition {
    field: string;
    operator: string;
    value: string;
}

interface PLDRuleForm {
    name: string;
    description: string;
    provider: string;
    context: string;
    person_type: string;
    field_path: string;
    field_type: string;
    comparison_type: string;
    comparison_value: string;
    recommendation: string;
    pld_flag: boolean;
    credit_flag: boolean;
    active: boolean;
    // ✅ Campos de regras compostas
    is_composite?: boolean;
    conditions?: Condition[];
    conditions_logic?: 'AND' | 'OR';
}

const RecommendationEnginePageForm: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEdit = Boolean(id);

    const [formData, setFormData] = useState<PLDRuleForm>({
        name: '',
        description: '',
        provider: 'INOVAMIND',
        context: 'PLD',
        person_type: 'PF',
        field_path: '',
        field_type: 'BOOLEAN',
        comparison_type: 'EQUALS',
        comparison_value: '',
        recommendation: 'REJECTED',
        pld_flag: true,
        credit_flag: false,
        active: true,
        // ✅ Inicializar campos compostos
        is_composite: false,
        conditions: [],
        conditions_logic: 'AND'
    });

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Providers estáticos (padrão BFF desacoplado)
    const providers = [
        { value: 'INOVAMIND', label: 'INOVAMIND' },
        { value: 'ESCAVADOR', label: 'ESCAVADOR' },
        { value: 'QUOD', label: 'QUOD' },
        { value: 'SERASA', label: 'SERASA' }
    ];

    const fieldTypes = [
        { value: 'BOOLEAN', label: 'Verdadeiro/Falso (Sim/Não)' },
        { value: 'TEXT', label: 'Texto' },
        { value: 'NUMBER', label: 'Número' },
        { value: 'ARRAY', label: 'Lista de valores' }
    ];

    const comparisonTypes = [
        { value: 'EQUALS', label: 'Igual a' },
        { value: 'NOT_EQUALS', label: 'Diferente de' },
        { value: 'CONTAINS', label: 'Contém' },
        { value: 'NOT_CONTAINS', label: 'Não contém' },
        { value: 'GREATER_THAN', label: 'Maior que' },
        { value: 'LESS_THAN', label: 'Menor que' },
        { value: 'GREATER_EQUAL', label: 'Maior ou igual a' },
        { value: 'LESS_EQUAL', label: 'Menor ou igual a' }
    ];

    // ✅ Operadores para regras compostas
    const compositeOperators = [
        { value: 'EQUALS', label: 'Igual a' },
        { value: 'CONTAINS', label: 'Contém' },
        { value: 'GTE', label: 'Maior ou igual a (>=)' },
        { value: 'LTE', label: 'Menor ou igual a (<=)' },
        { value: 'GT', label: 'Maior que (>)' },
        { value: 'LT', label: 'Menor que (<)' }
    ];

    const recommendations = [
        { value: 'REJECTED', label: 'Reprovado' },
        { value: 'PENDING', label: 'Pendente' }
    ];


    useEffect(() => {
        if (isEdit) {
            loadRule();
        }
    }, [id, isEdit]);

    const loadRule = async () => {
        setLoading(true);
        try {
            const authToken = localStorage.getItem('authToken');
            const response = await fetch(`${BASE_URL}/${id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': API_KEY,
                    'Authorization': `Bearer ${authToken}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                const rule = data.rule;
                
                // ✅ Detectar se é regra composta baseado na presença de conditions
                const isComposite = rule.conditions && rule.conditions.length > 0;
                
                setFormData({
                    ...rule,
                    is_composite: isComposite,
                    conditions: rule.conditions || [],
                    conditions_logic: rule.conditions_logic || 'AND'
                });
            } else {
                const errorData = await response.json();
                toast.error(errorData.message || 'Erro ao carregar regra');
                navigate('/recommendation-engine/rules/0/20/name/ASC');
            }
        } catch (error) {
            console.error('Erro ao carregar regra:', error);
            toast.error('Erro de conexão ao carregar regra');
            navigate('/recommendation-engine/rules/0/20/name/ASC');
        } finally {
            setLoading(false);
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Nome é obrigatório';
        }

        if (!formData.description.trim()) {
            newErrors.description = 'Descrição é obrigatória';
        }

        // ✅ Validação condicional: simples vs composta
        if (formData.is_composite) {
            if (!formData.conditions || formData.conditions.length === 0) {
                newErrors.conditions = 'Adicione pelo menos uma condição';
            }
        } else {
            if (!formData.field_path.trim()) {
                newErrors.field_path = 'Campo da API é obrigatório';
            }

            if (!formData.comparison_value.trim()) {
                newErrors.comparison_value = 'Valor de comparação é obrigatório';
            }
        }
        
        // ✅ Validar recommendation: obrigatório apenas se credit_flag = false
        if (!formData.credit_flag && !formData.recommendation) {
            newErrors.recommendation = 'Recomendação é obrigatória quando não sinalizar crédito';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        console.log('🔄 handleSubmit chamado');
        console.log('📋 formData:', formData);

        if (!validateForm()) {
            console.log('❌ Validação falhou');
            console.log('🔍 Erros:', errors);
            return;
        }

        console.log('✅ Validação passou');

        setLoading(true);
        try {
            const url = isEdit ? `${BASE_URL}/${id}` : BASE_URL;
            const method = isEdit ? 'PUT' : 'POST';
            
            console.log(`🌐 Fazendo ${method} para ${url}`);
            
            const authToken = localStorage.getItem('authToken');

            // ✅ Preparar payload baseado no tipo de regra
            const payload = formData.is_composite
                ? {
                    name: formData.name,
                    description: formData.description,
                    provider: formData.provider,
                    context: formData.context,
                    person_type: formData.person_type,
                    recommendation: formData.credit_flag ? '' : formData.recommendation,  // ✅ Vazio se credit_flag
                    pld_flag: formData.pld_flag,
                    credit_flag: formData.credit_flag,
                    active: formData.active,
                    conditions: formData.conditions,
                    conditions_logic: formData.conditions_logic,
                    created_by: 'USER',
                    updated_by: 'USER'
                }
                : {
                    ...formData,
                    recommendation: formData.credit_flag ? '' : formData.recommendation,  // ✅ Vazio se credit_flag
                    created_by: 'USER',
                    updated_by: 'USER'
                };

            console.log('📦 Payload:', JSON.stringify(payload, null, 2));

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': API_KEY,
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify(payload)
            });

            console.log('📡 Response status:', response.status);

            if (response.ok) {
                const responseData = await response.json();
                console.log('✅ Sucesso:', responseData);
                toast.success(isEdit ? 'Regra atualizada com sucesso!' : 'Regra criada com sucesso!');
                navigate('/recommendation-engine/rules/0/20/name/ASC');
            } else {
                const errorData = await response.text();
                console.error('❌ Erro response:', errorData);
                toast.error('Erro ao salvar regra');
            }
        } catch (error) {
            console.error('❌ Erro exception:', error);
            toast.error('Erro ao salvar regra');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field: keyof PLDRuleForm, value: string | boolean) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const getComparisonPlaceholder = () => {
        switch (formData.field_type) {
            case 'BOOLEAN':
                return 'true ou false';
            case 'NUMBER':
                return 'Ex: 200000';
            case 'TEXT':
                return 'Ex: ATIVO, CLP, culpado';
            case 'ARRAY':
                return 'Ex: CLP, DUP, RJF';
            default:
                return 'Valor para comparação';
        }
    };

    // ✅ Funções para gerenciar condições compostas
    const addCondition = () => {
        setFormData(prev => ({
            ...prev,
            conditions: [...(prev.conditions || []), { field: '', operator: 'EQUALS', value: '' }]
        }));
    };

    const removeCondition = (index: number) => {
        setFormData(prev => ({
            ...prev,
            conditions: prev.conditions?.filter((_, i) => i !== index) || []
        }));
    };

    const updateCondition = (index: number, field: keyof Condition, value: string) => {
        setFormData(prev => ({
            ...prev,
            conditions: prev.conditions?.map((cond, i) =>
                i === index ? { ...cond, [field]: value } : cond
            ) || []
        }));
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-800">
            <PageHeader
                title={isEdit ? 'Editar Regra PLD' : 'Nova Regra PLD'}
                breadcrumb={[
                    { label: 'Motor de Recomendação', url: '/recommendation-engine/rules/0/20/name/ASC' },
                    { label: 'Regras', url: '/recommendation-engine/rules/0/20/name/ASC' },
                    { label: isEdit ? 'Editar Regra' : 'Nova Regra', url: '#' }
                ]}
                apiVersion="Recommendation Engine - 1.0.0"
            >
                <div></div>
            </PageHeader>

            <div className="p-6">
                <form onSubmit={handleSubmit}>
                    <div className="bg-white dark:bg-slate-700 rounded-lg shadow">
                        <div className="p-6">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                {/* Informações Básicas */}
                                <div className="space-y-6">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 border-b pb-2">
                                        Informações da Regra
                                    </h3>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Nome da Regra *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => handleInputChange('name', e.target.value)}
                                            className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-slate-600 text-gray-900 dark:text-gray-100 ${errors.name
                                                ? 'border-red-500 focus:border-red-500'
                                                : 'border-gray-300 dark:border-slate-500 focus:border-blue-500'
                                                }`}
                                            placeholder="Ex: INOVAMIND - Sanções ONU"
                                        />
                                        {errors.name && (
                                            <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Descrição da Regra *
                                        </label>
                                        <textarea
                                            value={formData.description}
                                            onChange={(e) => handleInputChange('description', e.target.value)}
                                            className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-slate-600 text-gray-900 dark:text-gray-100 ${errors.description
                                                ? 'border-red-500 focus:border-red-500'
                                                : 'border-gray-300 dark:border-slate-500 focus:border-blue-500'
                                                }`}
                                            placeholder="Ex: Verifica se a pessoa possui sanções internacionais da ONU"
                                            rows={3}
                                        />
                                        {errors.description && (
                                            <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <SelectFilter
                                                id="provider"
                                                label="Provedor *"
                                                value={formData.provider}
                                                onChange={(value) => handleInputChange('provider', value)}
                                                options={providers}
                                                defaultVoidOption={true}
                                                defaultLabel="Selecione um provedor..."
                                            />
                                        </div>

                                        <div>
                                            <SelectFilter
                                                id="context"
                                                label="Contexto *"
                                                value={formData.context}
                                                onChange={(value) => handleInputChange('context', value)}
                                                options={[
                                                    { value: 'PLD', label: 'PLD' },
                                                    { value: 'CREDIT', label: 'Crédito' }
                                                ]}
                                                defaultVoidOption={true}
                                                defaultLabel="Selecione um contexto..."
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <SelectFilter
                                                id="person_type"
                                                label="Tipo de Pessoa *"
                                                value={formData.person_type}
                                                onChange={(value) => handleInputChange('person_type', value)}
                                                options={[
                                                    { value: 'PF', label: 'Pessoa Física (PF)' },
                                                    { value: 'PJ', label: 'Pessoa Jurídica (PJ)' }
                                                ]}
                                                defaultVoidOption={false}
                                            />
                                        </div>

                                        <div>
                                            <label htmlFor="field_path" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                Campo da API *
                                            </label>
                                            <input
                                                type="text"
                                                id="field_path"
                                                value={formData.field_path}
                                                onChange={(e) => handleInputChange('field_path', e.target.value)}
                                                placeholder="Ex: sanctions_list, uk_sanction_list, pep_search_v2"
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                            />
                                            {errors.field_path && (
                                                <p className="mt-1 text-sm text-red-600">{errors.field_path}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <p className="mt-1 text-xs text-gray-500">
                                            Digite o nome exato do campo que vem na resposta da API do provedor (ex: sanctions_list, uk_sanction_list)
                                        </p>
                                    </div>

                                    {/* ✅ Toggle para regra composta */}
                                    <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Regra Composta (Múltiplas Condições)
                                                </label>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    Ative para criar regras com múltiplas condições (ex: ETE + valor &gt;= 200k)
                                                </p>
                                            </div>
                                            <input
                                                type="checkbox"
                                                checked={formData.is_composite}
                                                onChange={(e) => handleInputChange('is_composite', e.target.checked)}
                                                className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Configuração da Regra */}
                                <div className="space-y-6">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 border-b pb-2">
                                        Configuração da Regra
                                    </h3>

                                    {/* ✅ Mostrar campos simples OU compostos */}
                                    {!formData.is_composite ? (
                                        <>
                                            <div>
                                                <SelectFilter
                                                    id="field_type"
                                                    label="Tipo do Campo *"
                                                    value={formData.field_type}
                                                    onChange={(value) => handleInputChange('field_type', value)}
                                                    options={fieldTypes}
                                                    defaultVoidOption={true}
                                                    defaultLabel="Selecione um tipo..."
                                                />
                                            </div>

                                            <div>
                                                <SelectFilter
                                                    id="comparison_type"
                                                    label="Tipo de Comparação *"
                                                    value={formData.comparison_type}
                                                    onChange={(value) => handleInputChange('comparison_type', value)}
                                                    options={comparisonTypes}
                                                    defaultVoidOption={true}
                                                    defaultLabel="Selecione um tipo..."
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Valor de Comparação *
                                                </label>
                                                <input
                                                    type="text"
                                                    value={formData.comparison_value}
                                                    onChange={(e) => handleInputChange('comparison_value', e.target.value)}
                                                    className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-slate-600 text-gray-900 dark:text-gray-100 ${errors.comparison_value
                                                        ? 'border-red-500 focus:border-red-500'
                                                        : 'border-gray-300 dark:border-slate-500 focus:border-blue-500'
                                                        }`}
                                                    placeholder={getComparisonPlaceholder()}
                                                />
                                                {errors.comparison_value && (
                                                    <p className="mt-1 text-sm text-red-600">{errors.comparison_value}</p>
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            {/* ✅ Seção de Condições Compostas */}
                                            <div className="space-y-4">
                                                <div className="flex items-center justify-between">
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                        Condições *
                                                    </label>
                                                    <SelectFilter
                                                        id="conditions_logic"
                                                        label=""
                                                        value={formData.conditions_logic || 'AND'}
                                                        onChange={(value) => handleInputChange('conditions_logic', value)}
                                                        options={[
                                                            { value: 'AND', label: 'Todas (AND)' },
                                                            { value: 'OR', label: 'Qualquer (OR)' }
                                                        ]}
                                                        defaultVoidOption={false}
                                                    />
                                                </div>

                                                {formData.conditions?.map((condition, index) => (
                                                    <div key={index} className="p-4 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-slate-600">
                                                        <div className="flex justify-between items-start mb-3">
                                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                                Condição {index + 1}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => removeCondition(index)}
                                                                className="text-red-600 hover:text-red-800 text-sm"
                                                            >
                                                                Remover
                                                            </button>
                                                        </div>

                                                        <div className="space-y-3">
                                                            <input
                                                                type="text"
                                                                placeholder="Campo (ex: sigla_assunto, valor_causa_valor)"
                                                                value={condition.field}
                                                                onChange={(e) => updateCondition(index, 'field', e.target.value)}
                                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100"
                                                            />

                                                            <select
                                                                value={condition.operator}
                                                                onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100"
                                                            >
                                                                {compositeOperators.map(op => (
                                                                    <option key={op.value} value={op.value}>{op.label}</option>
                                                                ))}
                                                            </select>

                                                            <input
                                                                type="text"
                                                                placeholder="Valor (ex: ETE, 200000)"
                                                                value={condition.value}
                                                                onChange={(e) => updateCondition(index, 'value', e.target.value)}
                                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100"
                                                            />
                                                        </div>
                                                    </div>
                                                ))}

                                                <button
                                                    type="button"
                                                    onClick={addCondition}
                                                    className="w-full px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 border border-blue-600 dark:border-blue-400 rounded-md hover:bg-blue-50 dark:hover:bg-slate-700"
                                                >
                                                    + Adicionar Condição
                                                </button>

                                                {errors.conditions && (
                                                    <p className="text-sm text-red-600">{errors.conditions}</p>
                                                )}
                                            </div>
                                        </>
                                    )}

                                    <div className="space-y-4">
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id="credit_flag"
                                                checked={formData.credit_flag}
                                                onChange={(e) => handleInputChange('credit_flag', e.target.checked)}
                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            />
                                            <label htmlFor="credit_flag" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                                Sinalizar para Crédito
                                            </label>
                                        </div>
                                        
                                        {/* ✅ Mostrar recommendation apenas se credit_flag = false */}
                                        {!formData.credit_flag && (
                                            <div>
                                                <SelectFilter
                                                    id="recommendation"
                                                    label="Recomendação quando acionada *"
                                                    value={formData.recommendation}
                                                    onChange={(value) => handleInputChange('recommendation', value)}
                                                    options={recommendations}
                                                    defaultVoidOption={true}
                                                    defaultLabel="Selecione uma recomendação..."
                                                />
                                            </div>
                                        )}

                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id="active"
                                                checked={formData.active}
                                                onChange={(e) => handleInputChange('active', e.target.checked)}
                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            />
                                            <label htmlFor="active" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                                Regra Ativa
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="px-6 py-4 bg-gray-50 dark:bg-slate-600 border-t border-gray-200 dark:border-slate-500 rounded-b-lg">
                            <div className="flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => navigate('/recommendation-engine/rules/0/20/name/ASC')}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-500 rounded-md hover:bg-gray-50 dark:hover:bg-slate-600"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? 'Salvando...' : (isEdit ? 'Atualizar Regra' : 'Criar Regra')}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RecommendationEnginePageForm;
